// @flow

import styles from "./Login.scss";

import React, { Component } from "react";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Typography from "@material-ui/core/Typography";
import { parsePhoneNumber } from "libphonenumber-js";

import type { FieldErrors } from "../../utils/FormError";

// Define props
type Props = {
    /** translate */
    translate: *,
    /** on submit handler */
    onSubmit: (password: string, verification: string) => void,
    /** on send sms-code again handler */
    onSendAgain: () => *,
    /** phone number of the user */
    phone: string,
    /** error message */
    error: ?string,
    /** field error messages */
    fieldErrors: ?FieldErrors,
    /** is the form submitting */
    busy: boolean,
};

// Define state
type State = {
    password: string,
    verification: string,
    shown: boolean,
};

/**
 * ChangePassword component
 */
export default class ChangePassword extends Component<Props, State> {
    state = {
        password: "",
        verification: "",
        shown: false,
    };

    /**
     * Get formatted phone number
     */
    get formattedPhone(): string {
        const { phone } = this.props;
        return parsePhoneNumber(phone).formatInternational();
    }

    /**
     * Has password field an error
     */
    get hasPasswordError(): boolean {
        const { fieldErrors } = this.props;
        return !!fieldErrors && !!fieldErrors.password;
    }

    /**
     * Has verification token field an error
     */
    get hasVerificationTokenError(): boolean {
        const { fieldErrors } = this.props;
        return !!fieldErrors && !!fieldErrors.verificationToken;
    }

    /**
     * Render
     */
    render() {
        const { onSubmit, onSendAgain, error, fieldErrors, busy, translate } =
            this.props;
        const { password, verification, shown } = this.state;

        return (
            <form
                className={styles.form}
                onSubmit={event => {
                    event.preventDefault();
                    onSubmit(password, verification);
                }}
            >
                <Typography variant="h2">
                    {translate("Change your password")}
                </Typography>
                <Typography>
                    {translate(
                        "Choose a new password to secure your account. " +
                            "Next, enter the reset SMS-code we just sent to " +
                            "%{phone} to confirm your identity.",
                        { phone: this.formattedPhone },
                    )}
                </Typography>
                <div className={styles.cell}>
                    <FormControl
                        error={!!fieldErrors && !!fieldErrors.password}
                        disabled={busy}
                        fullWidth
                    >
                        <InputLabel htmlFor="login-changepassword-password">
                            {translate("Password")}
                        </InputLabel>
                        <Input
                            id="login-changepassword-password"
                            type={shown ? "text" : "password"}
                            value={password}
                            onChange={({ target: { value } }) =>
                                this.setState({ password: value })
                            }
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        id="login-changepassword-password-show-hide"
                                        onClick={() =>
                                            this.setState({ shown: !shown })
                                        }
                                        onMouseDown={e => e.preventDefault()}
                                    >
                                        {shown ? (
                                            <VisibilityOff />
                                        ) : (
                                            <Visibility />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                        {this.hasPasswordError && (
                            <FormHelperText id="login-changepassword-password-helper-text">
                                {fieldErrors ? fieldErrors.password : ""}
                            </FormHelperText>
                        )}
                    </FormControl>
                </div>
                <div className={styles.cell}>
                    <FormControl
                        error={!!fieldErrors && !!fieldErrors.verificationToken}
                        disabled={busy}
                        fullWidth
                    >
                        <InputLabel htmlFor="login-changepassword-verification">
                            {translate("SMS-code")}
                        </InputLabel>
                        <Input
                            id="login-changepassword-verification"
                            type="text"
                            value={verification}
                            onChange={({ target: { value } }) =>
                                this.setState({ verification: value })
                            }
                        />
                        {this.hasVerificationTokenError && (
                            <FormHelperText id="login-changepassword-verification-helper-text">
                                {fieldErrors
                                    ? fieldErrors.verificationToken
                                    : ""}
                            </FormHelperText>
                        )}
                    </FormControl>
                </div>
                <div className={styles.cell}>
                    {error && (
                        <FormHelperText
                            id="login-changepassword-error"
                            error={!!error}
                        >
                            {error}
                        </FormHelperText>
                    )}
                </div>
                <div className={styles.cell}>
                    <Typography>
                        {translate("Didn’t get an SMS?")}{" "}
                        <a
                            id="login-changepassword-resend"
                            onClick={() => onSendAgain()}
                        >
                            {" "}
                            {translate("Send again")}
                        </a>
                    </Typography>
                </div>
                <div className={styles.cell}>
                    <Button
                        id="login-changepassword-submit"
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        disabled={busy}
                    >
                        {translate("Change password")}
                    </Button>
                </div>
            </form>
        );
    }
}
